import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG } from '../config';
import {
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  ListApiKeysResponse,
  RevokeApiKeyResponse
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class ApiKeyService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MANAGEMENT_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/auth/api-keys`;
    this.logDebug('ApiKeyService initialized', { apiUrl: this.apiUrl });
  }

  createApiKey(request: CreateApiKeyRequest): Observable<CreateApiKeyResponse> {
    this.logDebug('Creating API key', { label: request.label, scopes: request.scopes });

    return this.http.post<CreateApiKeyResponse>(this.apiUrl, request).pipe(
      map(response => {
        this.logDebug('API key created successfully', { prefix: response.data.keyPrefix });
        return response;
      }),
      catchError(error => this.handleError('createApiKey', error))
    );
  }

  getApiKeys(orgId: string): Observable<ListApiKeysResponse> {
    this.logDebug('Fetching API keys', { orgId });

    const params = new HttpParams().set('orgId', orgId);

    return this.http.get<ListApiKeysResponse>(this.apiUrl, { params }).pipe(
      map(response => {
        this.logDebug('API keys fetched', { count: response.data.length });
        return response;
      }),
      catchError(error => this.handleError('getApiKeys', error))
    );
  }

  revokeApiKey(keyId: string): Observable<RevokeApiKeyResponse> {
    this.logDebug('Revoking API key', { keyId });

    return this.http.delete<RevokeApiKeyResponse>(`${this.apiUrl}/${keyId}`).pipe(
      map(response => {
        this.logDebug('API key revoked successfully', { keyId });
        return response;
      }),
      catchError(error => this.handleError('revokeApiKey', error))
    );
  }

  private handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status) {
        errorMessage = `Server error: ${error.status} - ${error.statusText}`;
      }
    }

    this.logDebug(`${operation} failed`, { error: errorMessage, status: error.status });

    return throwError(() => ({
      operation,
      message: errorMessage,
      status: error.status,
      originalError: error
    }));
  }

  private logDebug(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log(`[ApiKeyService] ${message}`, data || '');
    }
  }
}
